import { useEffect, useMemo, useState } from "react";
import { loadCandidateVacancyTitle, sendProfessionalTestToCandidate } from "../../services/candidateApi";
import { getTests } from "../../services/testApi";
import { useAlertContext } from "../../context/AlertContext";
import { relationVacancyName } from "../../utils/candidateMapper";
import "./CalendarDialogs.css";

export default function ExerciseModal({ candidate, onClose, onSuccess }) {
    const [professionalTests, setProfessionalTests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(false);
    const [selectedTestId, setSelectedTestId] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [fetchedVacancyTitle, setFetchedVacancyTitle] = useState("");
    const { showAlert } = useAlertContext();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTestsLoading(true);
            try {
                const all = await getTests();
                const list = (Array.isArray(all) ? all : []).filter((t) => {
                    const type = String(t?.test_type || "").trim().toLowerCase();
                    const published = t?.publication_status !== "draft";
                    return published && (type === "вопросно-ответная форма" || type === "questions");
                });
                if (!cancelled) setProfessionalTests(list);
            } catch (e) {
                if (!cancelled) {
                    setProfessionalTests([]);
                    showAlert(`Не удалось загрузить тесты: ${e?.message || e}`, "error");
                }
            } finally {
                if (!cancelled) setTestsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        setFetchedVacancyTitle("");
        if (!candidate?.id) return undefined;
        (async () => {
            try {
                const title = await loadCandidateVacancyTitle(candidate.id);
                if (!cancelled && title) setFetchedVacancyTitle(title);
            } catch (_) {
                /* keep title from candidate payload */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [candidate?.id]);

    const vacancyTitle = useMemo(
        () =>
            fetchedVacancyTitle
            || relationVacancyName(candidate)
            || candidate?.vacancy_name
            || candidate?.last_vacancy_title
            || candidate?.position
            || "Вакансия не указана",
        [candidate, fetchedVacancyTitle]
    );

    const handleSend = async () => {
        const testId = Number(selectedTestId);
        if (!candidate?.id) return;
        if (!Number.isFinite(testId) || testId <= 0) {
            showAlert("Выберите профессиональный тест", "warning");
            return;
        }

        try {
            setSending(true);
            const applicationId = candidate?.application_id || candidate?.candidate_vacancy_application_id;
            const vacancyId = candidate?.vacancy_id || candidate?.last_vacancy_id;
            const args = [candidate.id, testId, message.trim() ? message.trim() : null];
            if (applicationId || vacancyId) {
                args.push({ applicationId, vacancyId });
            }
            const resp = await sendProfessionalTestToCandidate(...args);
            if (resp?.delivery && resp.delivery !== "sent" && resp.delivery !== "ok") {
                showAlert(
                    resp?.warning || "HH.ru не подтвердил отправку теста. Статус кандидата не изменён.",
                    "warning"
                );
                return;
            }
            showAlert("Профессиональный тест отправлен кандидату в HH", "success");
            onSuccess?.();
            onClose?.();
        } catch (e) {
            showAlert(`Ошибка отправки теста: ${e?.message || e}`, "error");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="calendar-dialog-backdrop">
            <div className="calendar-dialog w-full !max-w-2xl" role="dialog" aria-modal="true" aria-labelledby="exercise-dialog-title">
                <div className="calendar-dialog-header">
                    <div><span className="calendar-dialog-kicker">ДЕЙСТВИЕ ПО ВСТРЕЧЕ</span>
                    <h3 id="exercise-dialog-title">Отправить профессиональный тест</h3>
                    <p>
                        Тест будет отправлен кандидату в личные сообщения HH.ru
                    </p></div>
                    <button type="button" className="calendar-dialog-close" onClick={onClose} disabled={sending} aria-label="Закрыть">×</button>
                </div>

                <div className="calendar-dialog-body space-y-5">
                    <div className="calendar-candidate-preview !mt-0 !p-4">
                        <span className="calendar-candidate-avatar !w-11 !h-11">{String(candidate?.full_name || "К").split(/\s+/).slice(0, 2).map((part) => part[0]).join("")}</span>
                        <div><strong className="!text-sm">{candidate?.full_name || "—"}</strong><span>{vacancyTitle}</span></div>
                    </div>

                    <label className="block">
                        <span className="text-sm font-medium text-slate-700">Профессиональный тест *</span>
                        <select
                            aria-label="Профессиональный тест"
                            value={selectedTestId}
                            onChange={(e) => setSelectedTestId(e.target.value)}
                            disabled={testsLoading || sending}
                            className="calendar-field mt-1.5"
                        >
                            <option value="">Выберите тест</option>
                            {professionalTests.map((test) => (
                                <option key={test.id} value={test.id}>
                                    {test.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="block">
                        <span className="text-sm font-medium text-slate-700">Текст сообщения (необязательно)</span>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                            placeholder="Если оставить пустым, будет отправлен стандартный текст со ссылкой на тест."
                            className="calendar-field !h-auto mt-1.5 py-3 resize-y"
                        />
                        <span className="mt-1.5 block text-xs text-slate-500">
                            Ваш текст будет отправлен перед названием и ссылкой на выбранный тест.
                        </span>
                    </label>

                    {testsLoading ? (
                        <p className="text-xs text-slate-400">Загрузка тестов…</p>
                    ) : professionalTests.length === 0 ? (
                        <p className="text-xs text-amber-700 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                            Нет доступных профессиональных тестов типа «Вопросно-ответная форма».
                        </p>
                    ) : null}
                </div>

                <div className="calendar-dialog-footer">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={sending}
                        className="calendar-button calendar-button--secondary"
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || testsLoading || !selectedTestId}
                        className="calendar-button calendar-button--primary"
                    >
                        {sending ? "Отправка..." : "Отправить тест"}
                    </button>
                </div>
            </div>
        </div>
    );
}
